import type { RdQuoteStepProps } from '@/features/quotes/components/rd-equipamentos/rd-quote-step.types';
import { QuoteBreakdown } from '@/features/quotes/components/rd-equipamentos/steps/sections/quote-breakdown.component';
import { GeneratePdfDialog } from '@/features/quotes/components/quote-steps/generate-pdf.dialog';
import { useQuoteParams } from '@/features/quotes/context/quote-params.context';
import { useSelectedBrokerWriteGuard } from '@/features/brokers/hooks';
import { useCanApproveRd } from '@/features/quotes/hooks/rd-equipamentos/use-can-approve-rd.hook';
import { useRdLifecycle } from '@/features/quotes/hooks/rd-equipamentos/use-rd-lifecycle.hook';
import { useRdProposal } from '@/features/quotes/hooks/rd-equipamentos/use-rd-proposal.hook';
import { useRdQuote } from '@/features/quotes/hooks/rd-equipamentos/use-rd-quote.hook';
import { useRdDocumentPdf } from '@/features/policies/hooks/use-rd-document-pdf.hook';
import type { RdStatus } from '@/services/quotes/rd-equipamentos/types';
import { toaster } from '@/shared/ui/toaster.component';
import {
  Button,
  Dialog,
  Spinner,
  Text,
} from '@noorden-insurtech/alba-design-system';
import { ArrowLeft, ArrowRight, FileDown } from 'lucide-react';
import { useRef, useState } from 'react';
import {
  StepActions,
  StepActionsGroup,
} from './sections/step-actions.component';

/**
 * Status a partir dos quais a proposta ainda pode ser cancelada — todo
 * status "aberto" (antes de emitida a apólice). `emitida`/`cancelada` ficam
 * de fora: cancelar uma apólice já emitida exige um fluxo próprio
 * (fora do escopo deste wizard), e uma proposta já cancelada não tem o que
 * cancelar de novo. Adição deliberada sobre o demo — cuja rota `/cancelar`
 * nunca é exposta na UI.
 */
const CANCELABLE_STATUSES: readonly RdStatus[] = [
  'rascunho',
  'cotada',
  'aguardando_analise',
  'aprovada',
  'aceita',
];

/**
 * Step 5 — Cotação (RD Equipamentos, ramo 0171): breakdown da cotação
 * (`QuoteBreakdown`, compartilhado com o Step 6 — Emissão) + barra de ações
 * por status da proposta (demo `etapa_cotacao.html`):
 * - `rascunho` → Calcular (alcançável quando os parâmetros nunca foram
 *   salvos, já que `PUT /params` normalmente já dispara o cálculo).
 * - `cotada` → Recalcular + Gerar proposta.
 * - `aguardando_analise` → Recalcular + Aprovar (Aprovar só aparece para
 *   perfis de subscrição da seguradora — `useCanApproveRd`).
 * - `aprovada` → Gerar proposta (sem Recalcular: o backend só permite recalcular a
 *   partir de `cotada`/`aguardando_analise` — `RD_STATUS_TRANSITIONS`).
 * - `aceita`/`emitida` → Baixar Proposta + Ir para emissão (avança para o
 *   próximo step via `onNext`, sem chamada de mutação). "Gerar proposta" só
 *   aceita no motor; a transmissão para emissão é no Step 6.
 * - Cancelar (com `Dialog` de confirmação) em qualquer status aberto —
 *   adição deliberada sobre o demo.
 */
export function StepCotacao({
  onNext,
  onBack,
  canGoBack = true,
  isViewMode = false,
}: RdQuoteStepProps) {
  const { id, policyId } = useQuoteParams();
  const resolvedProposalId = (id || policyId || '').trim();

  const { data: proposal, isLoading: isProposalLoading } = useRdProposal(
    resolvedProposalId || undefined
  );
  const { data: quote, isLoading: isQuoteLoading } = useRdQuote(
    resolvedProposalId || undefined,
    !!resolvedProposalId
  );

  const { calculate, approve, accept, cancel } = useRdLifecycle(
    resolvedProposalId || undefined
  );
  const { canApprove, isLoading: isCanApproveLoading } = useCanApproveRd();
  // Corretora inativa: sem (re)calcular, aceitar, aprovar ou cancelar — a Api
  // barra com 403. Vale também no modo visualização, onde essas ações ficam.
  const quoteWriteGuard = useSelectedBrokerWriteGuard('quote');
  const actionGuard = useSelectedBrokerWriteGuard('proposal', 'other');
  const quoteActionGuard = useSelectedBrokerWriteGuard('quote', 'other');
  const documentPdf = useRdDocumentPdf();

  const [cancelOpen, setCancelOpen] = useState(false);
  // `null` fechado, `'cotacao'` aberto. Só existe um tipo nesta etapa
  // (PO 2026-09-17 removeu o botão de Proposta daqui) — o valor não-null é
  // sempre `'cotacao'`, mas o tipo já é o esperado por `useRdDocumentPdf`.
  const [pdfDialogTipo, setPdfDialogTipo] = useState<
    'cotacao' | 'proposta' | null
  >(null);

  /**
   * Trava síncrona contra duplo-clique, mesmo padrão do fix J-2 na emissão
   * (`step-emissao.component.tsx:136`): `isPending` só vira `true` no próximo
   * render, então dois cliques rápidos disparam duas requisições antes disso.
   *
   * Um ref único cobre todas as ações porque são transições mutuamente
   * exclusivas da mesma proposta — isso também impede disparar "Aprovar" e
   * "Gerar proposta" quase simultaneamente. Importa mais aqui do que parece: as
   * transições do motor não são serializadas, então duas chamadas
   * concorrentes podem ambas ser aceitas.
   */
  const isActionRunningRef = useRef(false);

  async function runAction(
    mutateAsync: () => Promise<unknown>,
    errorTitle: string
  ) {
    if (isActionRunningRef.current) return;
    isActionRunningRef.current = true;
    try {
      await mutateAsync();
    } catch (error) {
      toaster.create({
        type: 'error',
        title: errorTitle,
        description: error instanceof Error ? error.message : 'Erro inesperado',
      });
    } finally {
      isActionRunningRef.current = false;
    }
  }

  async function handleCancel() {
    if (isActionRunningRef.current) return;
    isActionRunningRef.current = true;
    try {
      await cancel.mutateAsync();
      setCancelOpen(false);
    } catch (error) {
      toaster.create({
        type: 'error',
        title: 'Não foi possível cancelar a proposta',
        description: error instanceof Error ? error.message : 'Erro inesperado',
      });
    } finally {
      isActionRunningRef.current = false;
    }
  }

  const status = proposal?.status;

  const isBusy =
    calculate.isPending ||
    approve.isPending ||
    accept.isPending ||
    cancel.isPending;
  const blockInteraction = isProposalLoading || isQuoteLoading || isBusy;
  // `isViewMode` NÃO entra aqui: Calcular/Recalcular/Gerar proposta/Aprovar/
  // Cancelar continuam disponíveis em modo visualização — só o
  // carregamento/processamento em curso bloqueia.
  const disabled = blockInteraction;
  const calculateDisabled = disabled || quoteWriteGuard.blocked;
  const lifecycleDisabled = disabled || actionGuard.blocked;

  const showCalcular = status === 'rascunho';
  const showRecalcular = status === 'cotada' || status === 'aguardando_analise';
  const showGerarProposta = status === 'cotada' || status === 'aprovada';
  const showAprovar =
    status === 'aguardando_analise' && !isCanApproveLoading && canApprove;
  const showIrParaEmissao = status === 'aceita' || status === 'emitida';
  // A proposta só existe depois de "Gerar proposta" (`aceita`).
  const showBaixarProposta = showIrParaEmissao;
  // Antes o único PDF do RD era o da apólice, disponível só após a emissão
  // no i4Pro — o corretor não tinha como levar nada da cotação para o
  // cliente enquanto isso não acontecesse. Cotação e Proposta são dois
  // documentos DIFERENTES na Folheteria (PO 2026-09-17); os dois botões
  // ficam disponíveis sempre que já há uma cotação calculada.
  const showDownloads = !showCalcular;
  const showCancelar = !!status && CANCELABLE_STATUSES.includes(status);

  return (
    <div className='relative flex flex-col gap-8'>
      {isProposalLoading || isQuoteLoading ? (
        <div
          className='absolute inset-0 z-10 flex flex-col items-center justify-center gap-3 rounded-2xl bg-white/80'
          aria-busy
          aria-label='Carregando cotação'
        >
          <Spinner size='md' />
          <Text className='text-sm text-alba-black-400'>
            Carregando cotação...
          </Text>
        </div>
      ) : null}

      {proposal && quote ? (
        <QuoteBreakdown quote={quote} proposal={proposal} />
      ) : null}

      <StepActions>
        <Button
          variant='ghost'
          size='lg'
          className='flex items-center gap-2'
          onClick={onBack}
          disabled={isViewMode || !canGoBack || blockInteraction}
        >
          <ArrowLeft className='h-5 w-5' strokeWidth={1.6} />
          Voltar
        </Button>

        <StepActionsGroup>
          {showCancelar ? (
            <Button
              variant='ghost'
              size='md'
              className='flex items-center gap-2 text-alba-error-red-500'
              onClick={() => setCancelOpen(true)}
              disabled={lifecycleDisabled}
              title={actionGuard.reason}
            >
              Cancelar
            </Button>
          ) : null}

          {showDownloads ? (
            <Button
              variant='secondary'
              size='md'
              className='flex items-center gap-2 whitespace-nowrap bg-alba-green-500 text-alba-white-500 [&>svg]:text-alba-white-500 hover:[&>svg]:text-[#129440]'
              onClick={() => setPdfDialogTipo('cotacao')}
              // Ação de LEITURA (gera/baixa um PDF, não muda a proposta) —
              // `isViewMode` fica de fora de propósito, mesmo padrão do
              // "Imprimir apólice" em `step-emissao.component.tsx`: olhar
              // uma proposta em modo visualização não deveria impedir
              // baixar o documento dela. Corretora inativa trava (a Api barra
              // o `document-pdf` com 403).
              disabled={blockInteraction || quoteActionGuard.blocked}
              title={quoteActionGuard.reason}
            >
              <FileDown className='h-5 w-5' strokeWidth={2} />
              Baixar Cotação
            </Button>
          ) : null}

          {showBaixarProposta ? (
            <Button
              variant='secondary'
              size='md'
              className='flex items-center gap-2 whitespace-nowrap bg-alba-green-500 text-alba-white-500 [&>svg]:text-alba-white-500 hover:[&>svg]:text-[#129440]'
              onClick={() => setPdfDialogTipo('proposta')}
              disabled={blockInteraction || quoteActionGuard.blocked}
              title={quoteActionGuard.reason}
            >
              <FileDown className='h-5 w-5' strokeWidth={2} />
              Baixar Proposta
            </Button>
          ) : null}

          {showCalcular ? (
            <Button
              variant='primary'
              size='md'
              className='flex items-center gap-2'
              loading={calculate.isPending}
              disabled={calculateDisabled}
              title={quoteWriteGuard.reason}
              onClick={() =>
                runAction(
                  () => calculate.mutateAsync(),
                  'Não foi possível calcular a cotação'
                )
              }
            >
              Calcular
            </Button>
          ) : null}

          {showRecalcular ? (
            <Button
              variant='outline'
              size='md'
              className='flex items-center gap-2'
              loading={calculate.isPending}
              disabled={calculateDisabled}
              title={quoteWriteGuard.reason}
              onClick={() =>
                runAction(
                  () => calculate.mutateAsync(),
                  'Não foi possível recalcular a cotação'
                )
              }
            >
              Recalcular
            </Button>
          ) : null}

          {showGerarProposta ? (
            <Button
              variant='primary'
              size='md'
              className='flex items-center gap-2'
              loading={accept.isPending}
              disabled={lifecycleDisabled}
              title={actionGuard.reason}
              onClick={() =>
                runAction(
                  () => accept.mutateAsync(),
                  'Não foi possível gerar a proposta'
                )
              }
            >
              Gerar proposta
            </Button>
          ) : null}

          {showAprovar ? (
            <Button
              variant='primary'
              size='md'
              className='flex items-center gap-2'
              loading={approve.isPending}
              disabled={lifecycleDisabled}
              title={actionGuard.reason}
              onClick={() =>
                runAction(
                  () => approve.mutateAsync(),
                  'Não foi possível aprovar a proposta'
                )
              }
            >
              Aprovar
            </Button>
          ) : null}

          {showIrParaEmissao ? (
            <Button
              variant='primary'
              size='md'
              className='flex items-center gap-2'
              disabled={isViewMode}
              onClick={() => onNext()}
            >
              Ir para emissão
              <ArrowRight className='h-6 w-6' strokeWidth={2} />
            </Button>
          ) : null}
        </StepActionsGroup>
      </StepActions>

      <Dialog.Root
        open={cancelOpen}
        onOpenChange={details => setCancelOpen(details.open)}
      >
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content className='mx-4 w-[calc(100%-2rem)] max-w-md sm:mx-0 sm:w-full'>
            <Dialog.Header>
              <Dialog.Title>Cancelar proposta</Dialog.Title>
            </Dialog.Header>
            <Dialog.Body>
              <Text className='text-sm text-alba-gray-600'>
                Deseja cancelar esta proposta? Esta ação não poderá ser
                desfeita.
              </Text>
            </Dialog.Body>
            {/*
              Empilhado no mobile: com `flex justify-end` numa linha só, o
              rotulo longo do botao de confirmacao consumia a largura toda a
              375px e empurrava "Voltar" para fora da viewport (x=-103) —
              sobrava visivel apenas a acao destrutiva.
            */}
            <Dialog.Footer className='flex flex-col gap-3 sm:flex-row sm:justify-end'>
              <Dialog.CloseTrigger asChild>
                <Button
                  variant='outline'
                  disabled={cancel.isPending}
                  className='w-full sm:w-auto'
                >
                  Voltar
                </Button>
              </Dialog.CloseTrigger>
              <Button
                variant='primary'
                loading={cancel.isPending}
                disabled={cancel.isPending}
                onClick={handleCancel}
                className='w-full sm:w-auto'
              >
                Confirmar cancelamento
              </Button>
            </Dialog.Footer>
          </Dialog.Content>
        </Dialog.Positioner>
      </Dialog.Root>

      <GeneratePdfDialog
        isOpen={pdfDialogTipo !== null}
        onClose={() => setPdfDialogTipo(null)}
        isLoading={documentPdf.isPending}
        title={
          pdfDialogTipo === 'proposta' ? 'Baixar Proposta' : 'Baixar Cotação'
        }
        description={
          <>
            <Text className='text-sm text-alba-gray-600'>
              {pdfDialogTipo === 'proposta'
                ? 'O PDF da proposta será gerado para download.'
                : 'O PDF da cotação será gerado para download.'}
            </Text>
            <Text className='text-sm text-alba-gray-600'>
              Deseja continuar?
            </Text>
          </>
        }
        onConfirm={async () => {
          if (!resolvedProposalId || !pdfDialogTipo) return;
          try {
            await documentPdf.mutateAsync({
              proposalId: resolvedProposalId,
              tipo: pdfDialogTipo,
            });
            setPdfDialogTipo(null);
          } catch {
            // Toast já disparado pelo `onError` de `useRdDocumentPdf` — o
            // dialog fica aberto para o corretor tentar de novo.
          }
        }}
      />
    </div>
  );
}
